import React from 'react'
import axios from 'axios'
import { API_POOL_BASE_URL } from 'constants/links'
import { Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts'
import { roundToDecimalPlace } from 'utils/data'
import {
  ChartHeader,
  ChartHeaderTitle,
  ChartTitle,
  RechartsWrapper,
  StyledTabButton,
  Tabs,
  TooltipContainer,
} from '../Metrics/styled'
import { LoadingRows } from 'pages/Pool/styleds'

const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date)
}
const MetricsFees = () => {
  const [dailyData, setDailyData] = React.useState<{ date: string; v2: number; v3: number; total: number }[]>([])

  const [loading, setLoading] = React.useState<boolean>(true)
  const [selectedRange, setSelectedRange] = React.useState<'7D' | '30D'>('7D')

  const fetchMetrics = async () => {
    try {
      const response = await axios.get(`${API_POOL_BASE_URL}/api/fees-history`)

      setDailyData(response.data.data)

      setLoading(false)
    } catch (error) {
      console.log(error)
    }
  }

  React.useEffect(() => {
    if (loading) {
      fetchMetrics()
    }
  }, [loading])

  const fillMissingDays = (data: { name: string; v2: number; v3: number; total: number }[], days: number) => {
    if (data.length === 0) return []

    const filledData = [...data]

    const latestDate = new Date(filledData[filledData.length - 1].name)

    const today = new Date()

    for (let i = 1; filledData.length < days; i++) {
      const pastDate = new Date(latestDate)
      pastDate.setDate(latestDate.getDate() - i)

      if (pastDate > today) break

      if (
        !filledData.some(
          (entry) => new Date(entry.name).toISOString().slice(0, 10) === pastDate.toISOString().slice(0, 10)
        )
      ) {
        filledData.push({
          name: pastDate.toISOString(),
          v2: 0,
          v3: 0,
          total: 0,
        })
      }
    }

    filledData.sort((a, b) => new Date(a.name).getTime() - new Date(b.name).getTime())

    return filledData
  }

  const chartDataRaw = dailyData.map((fee: { date: string; v2: number; v3: number; total: number }) => {
    return {
      name: new Date(new Date(fee.date).setDate(new Date(fee.date).getDate() - 1)).toISOString(),
      v2: fee.v2,
      v3: fee.v3,
      total: fee.total,
    }
  })

  const chartData = fillMissingDays(chartDataRaw, 30)
  const chartDataFiltered = selectedRange === '7D' ? chartData.slice(-7) : chartData
  let totalFees = 0

  chartDataFiltered.forEach((value) => {
    totalFees = totalFees + value.total
  })
  return (
    <RechartsWrapper>
      {loading ? (
        <>
          <LoadingRows>
            <div />
            <div />
            <div />
            <div />
            <div />
          </LoadingRows>
        </>
      ) : (
        <>
          <ChartHeader>
            <ChartHeaderTitle>
              <ChartTitle>Generated Fees</ChartTitle>
              <Tabs>
                <StyledTabButton onClick={() => setSelectedRange('7D')} isActive={selectedRange === '7D'}>
                  7D
                </StyledTabButton>
                <StyledTabButton onClick={() => setSelectedRange('30D')} isActive={selectedRange === '30D'}>
                  30D
                </StyledTabButton>
              </Tabs>
            </ChartHeaderTitle>
            <ChartTitle>{totalFees ? `${roundToDecimalPlace(`${totalFees}`, '0')}$` : ''}</ChartTitle>
          </ChartHeader>
          <ResponsiveContainer width="100%" height={185}>
            <AreaChart data={chartDataFiltered}>
              <defs>
                <linearGradient id="colorVolume" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#4AFB39" stopOpacity={0.7} />
                  <stop offset="100%" stopColor="#4AFB39" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorVolume1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00D1FF" stopOpacity={0.6} />
                  <stop offset="100%" stopColor="#00D1FF" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorVolume2" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#A259FF" stopOpacity={0.6} />
                  <stop offset="100%" stopColor="#A259FF" stopOpacity={0} />
                </linearGradient>
              </defs>

              <Area type="monotone" dataKey="v2" stroke="#00D1FF" fill="url(#colorVolume1)" />
              <Area type="monotone" dataKey="v3" stroke="#A259FF" fill="url(#colorVolume2)" />
              <Area type="monotone" dataKey="total" stroke="#4AFB39" fill="url(#colorVolume)" />

              <Tooltip
                content={({ payload }) => {
                  if (payload && payload.length > 0) {
                    const data = payload[0].payload
                    return (
                      <TooltipContainer>
                        <span className="tooltip__heading">{formatDate(data.name)}</span>
                        <div className="tooltip__volume">
                          <span className="tooltip__volume-title" style={{ fontWeight: 500, color: '#4AFB39' }}>
                            Total
                          </span>
                          <span className="tooltip__volume-value">${roundToDecimalPlace(data.total, '0')}</span>

                          <span className="tooltip__volume-title" style={{ fontWeight: 500, color: '#A259FF' }}>
                            V3
                          </span>
                          <span className="tooltip__volume-value">${roundToDecimalPlace(data.v3, '0')}</span>
                          <span className="tooltip__volume-title" style={{ fontWeight: 500, color: '#00D1FF' }}>
                            V2
                          </span>
                          <span className="tooltip__volume-value">${roundToDecimalPlace(data.v2, '0')}</span>
                        </div>
                      </TooltipContainer>
                    )
                  }
                  return null
                }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </>
      )}
    </RechartsWrapper>
  )
}

export default MetricsFees
